import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector, usePermissions, useIconSize } from "@app/hooks";
import { selectCurrentUser } from "@app/features/auth/authSlice";
import Button from "@components/button/Button";

import { MdTranslate, MdGroup, MdPersonOutline } from "react-icons/md";
import {
	BiCaretDown,
	BiChevronDown,
	BiUserCircle,
} from "react-icons/bi";
import {
	openUserModal,
	openManageUsersModal,
} from "@app/features/header/headerSlice";
import {
	selectCurrentProject,
} from "@app/features/project/projectSlice";
import { usePaginatedProjects } from "@hooks/index";
import { useLocation, useNavigate } from "react-router-dom";
import type { User } from "@typedefs/interfaces";
import { useTranslation } from "react-i18next";
import { TranslationKey } from "@typedefs/modulesInterfaces";
import { selectSelectedLanguage, setSelectedLanguage } from "@app/features/builder/builderSlice";
import BaseLabel from "@components/label/BaseLabel";
import OutsideClickSub from "@components/outsideClickSub/OutsideClickSub";
import TranslatableText from "@components/translations/TranslatableText";
import Spinner from "@components/spinner/Spinner";

interface HeaderAction {
	id: string;
	value: TranslationKey | null;
	icon: ReactNode;
	disabled?: boolean;
	hasDrop?: boolean;
	isHighlighted?: boolean;
	lines?: Line[];
	onClick?: () => void;
	rowClick?: (line: Line) => void;
}
interface HeaderAction {
	id: string;
	value: TranslationKey | null;
	icon: ReactNode;
	hasDrop?: boolean;
	onClick?: () => void;
}

const HeaderActions = ({ isHeaderSmall }: { isHeaderSmall: boolean }) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const user: User | null = useAppSelector(selectCurrentUser);
	const { pathname } = useLocation();
	const { canShare, isPublic } = usePermissions();
	const { project } = useAppSelector(selectCurrentProject);
	const isProjectSelected = useMemo(() => !!project?.id, [project]);
	const isInHihInvestmentForum = useMemo(() => pathname.includes("/hih-investment-forum"), [pathname]);

	const iconSize = useIconSize(20);

	const actions: HeaderAction[] = useMemo(() => {
		const userBtn: HeaderAction = {
			id: "user",
			value: null,
			icon: <BiUserCircle size={iconSize} />,
			hasDrop: true,
			onClick: () => dispatch(openUserModal()),
		};

		const manageUsersBtn: HeaderAction = {
			id: "manageUsers",
			value: "main.users_management",
			icon: <MdGroup size={iconSize} className="me-1" />,
			hasDrop: true,
			onClick: () => dispatch(openManageUsersModal()),
		};
		return isPublic
			? []
			: user && canShare && isProjectSelected && !isInHihInvestmentForum
				? [manageUsersBtn, userBtn]
				: user && !isInHihInvestmentForum
					? [userBtn]
					: [];
	}, [user, canShare, isProjectSelected, dispatch, isInHihInvestmentForum, iconSize, isPublic]);

	const handleLogin = () => navigate("/auth/login");

	return (
		<div className="h-100 d-flex">
			{isPublic && !isInHihInvestmentForum ?
				<div className="h-100 d-flex align-items-center pe-1">
					<div className="pos-relative">
						<Button className="btn-switch-project responsive-btn-height-2" onClick={handleLogin}>
							<MdPersonOutline size={iconSize} className="me-1" />
							<TranslatableText translationKey="main.login" className="me-1 fs-10" />
						</Button>
					</div>
				</div>
				: null}
			{isHeaderSmall || isPublic ? null : <SwitchProjectButton />}
			{[...actions].map((action) => {
				return (
					<div
						key={action.id}
						className="h-100 d-flex align-items-center"
						style={{ paddingRight: "9px" }}
					>
						<HeaderButton {...action} />
					</div>
				);
			})}
			<SwitchLanguageButton />
		</div>
	);
};

export const HeaderButton = ({
	id,
	value,
	icon,
	hasDrop,
	isHighlighted,
	disabled,
	onClick,
	lines,
}: HeaderAction) => {
	const [isModalShown, setIsModalShown] = useState(false);
	const showModal = () => setIsModalShown(true);
	const iconSize = useIconSize(16);

	return (
		<div className="pos-relative">
			<Button
				id={id}
				onClick={onClick ? onClick : showModal}
				className={`btn-header ${isHighlighted && "highlighted"} br-6`}
				disabled={disabled}
			>
				{icon}
				{value && <TranslatableText translationKey={value} className="fs-10" />}
				{hasDrop ? <BiCaretDown size={iconSize} className="ms-1" /> : null}
			</Button>
			{isModalShown ? (
				<Modal
					handleClose={() => setIsModalShown(false)}
					lines={lines ? lines : []}
				/>
			) : null}
		</div>
	);
};

const SwitchProjectButton = () => {
	const [isModalShown, setIsModalShown] = useState(false);
	const showModal = () => setIsModalShown(true);

	const { project } = useAppSelector(selectCurrentProject);
	const { projectOptions, isLoading, hasNextPage, handleLoadMore } = usePaginatedProjects();

	const navigate = useNavigate();

	const projectLines: Line[] = useMemo(() => projectOptions.length > 0
		? [
			...projectOptions.map((project) => ({
				label: project.name,
				id: project.id,
			})),
		].concat(hasNextPage ? [{ label: "main.load_more_with_brackets", id: -2 }] : [])
			.concat({ label: "main.go_to_project_list", id: -1 })
		: [], [projectOptions, hasNextPage]);

	const handleProjectClick = (line: Line) => {
		if (line.id === -1) return navigate("/");
		if (line.id === -2) return handleLoadMore();

		const selectedProject = projectOptions.find((proj) => proj.id === line.id);
		if (selectedProject && selectedProject.id !== project?.id)
			navigate(`project/${selectedProject.id}/activities`);
	};

	const iconSize = useIconSize(18);

	return (
		<div className="h-100 d-flex align-items-center pe-1">
			<div className="pos-relative">
				<Button className="btn-switch-project responsive-btn-height-2" onClick={showModal} disabled={isLoading}>
					<TranslatableText className="no-wrap fs-10" translationKey={"main.switch_projects"} />
					{isLoading ? <Spinner size={iconSize} className="ms-1" /> : <BiChevronDown size={iconSize} className="ms-1" />}
				</Button>
				{isModalShown ? (
					<Modal
						handleClose={() => setIsModalShown(false)}
						lines={projectLines}
						onLineClick={handleProjectClick}
					/>
				) : null}
			</div>
		</div>
	);
};

const SwitchLanguageButton = () => {
	const [isModalShown, setIsModalShown] = useState(false);
	const showModal = () => setIsModalShown(true);
	const dispatch = useAppDispatch();
	const selectedLanguage = useAppSelector(selectSelectedLanguage);
	const iconSize = useIconSize(18);

	const languageOptions: Line[] = [
		{ label: "English", id: 1 },
		{ label: "Español", id: 2 },
		{ label: "Français", id: 3 },
		{ label: "Русский", id: 4 },
	];

	const { i18n } = useTranslation();

	const getLanguageLabel = (code: string) => {
		switch (code) {
			case 'en': return 'English';
			case 'es': return 'Español';
			case 'fr': return 'Français';
			case 'ru': return 'Русский';
			default: return code;
		}
	};

	const changeLanguage = (line: Line) => {
		let languageCode = "en";
		switch (line.id) {
			case 1:
				languageCode = "en";
				break;
			case 2:
				languageCode = "es";
				break;
			case 3:
				languageCode = "fr";
				break;
			case 4:
				languageCode = "ru";
				break;
			default:
				break;
		}
		i18n.changeLanguage(languageCode);
		dispatch(setSelectedLanguage(languageCode));
	};

	return (
		<div className="h-100 d-flex align-items-center pe-1">
			<div className="pos-relative">
				<Button className="btn-switch-project responsive-btn-height-2" onClick={showModal}>
					<MdTranslate size={iconSize} className="me-1" />
					<span className="no-wrap fs-10">{getLanguageLabel(selectedLanguage || i18n.language)}</span>
					<BiChevronDown size={iconSize} className="ms-1" />
				</Button>
				{isModalShown ? (
					<Modal
						handleClose={() => setIsModalShown(false)}
						lines={languageOptions}
						onLineClick={changeLanguage}
					/>
				) : null}
			</div>
		</div>
	);
};

const Modal = ({
	lines,
	handleClose,
	onLineClick,
}: {
	lines: Line[];
	handleClose: () => void;
	activeColor?: string;
	onLineClick?: (line: Line) => void;
}) => {
	return (
		<OutsideClickSub className="drop-menu" callback={handleClose}>
			<>
				{lines.map((line) => (
					<ModalLine
						line={line}
						handleClose={handleClose}
						key={line.id}
						onLineClick={onLineClick ? onLineClick : () => { }}
					/>
				))}
			</>
		</OutsideClickSub>
	);
};

export interface Line {
	id: number;
	label: string;
}

const ModalLine = ({
	line,
	handleClose,
	activeColor,
	onLineClick,
}: {
	line: Line;
	handleClose: () => void;
	onLineClick: (line: Line) => void;
	activeColor?: string;
}) => {
	const [isHovered, setIsHovered] = useState(false);
	const { project } = useAppSelector(selectCurrentProject);
	const isCurrentProject = useMemo(() => project?.id === line.id, [project, line]);

	return (
		<div
			className={"clickable drop-menu-line px-2"}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			style={{
				backgroundColor: isHovered
					? activeColor
						? activeColor
						: "var(--gray)"
					: isCurrentProject
						? "var(--primary-bright)"
						: "white",
				maxWidth: "500px",
			}}
			onClick={() => {
				handleClose();
				onLineClick(line);
			}}
		>
			<BaseLabel
				className={`fs-11 ff-medium-cond truncate ${isCurrentProject && !isHovered ? "text-white" : ""}`}
				label={line.label}
			/>
		</div>
	);
};

export default HeaderActions;
